import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../theme/ThemeProvider';

const TopBar = ({ props, onMenuClick }) => {
  const navigate = useNavigate();
  const { mode, toggleColorMode } = useThemeMode();
  const isDark = mode === 'dark';

  const profile = props?.profile || {};
  const userType = profile?.user_type || profile?.data?.user_type;
  const isMasterOrg = userType === 'master_org';
  const headerText = isMasterOrg ? 'Club Partner Admin Panel' : 'Admin Panel';
  const orgName =
    profile?.organisationDetails?.name ||
    profile?.data?.organisationDetails?.name ||
    profile?.organisation_name ||
    profile?.data?.organisation_name ||
    '';
  const profileImage =
    profile?.profile_image ||
    profile?.data?.profile_image ||
    profile?.logo_url ||
    profile?.data?.logo_url ||
    profile?.organisationDetails?.logo_url ||
    '';
  const userInitial =
    (orgName ||
      profile?.first_name ||
      profile?.data?.first_name ||
      'U')[0]?.toUpperCase() || 'U';

  const handleOpenSettings = () => {
    if (!isMasterOrg) return;
    navigate('/master-org/account');
  };

  const sportsAppLogo = (
    <Box
      component="img"
      src={`${process.env.PUBLIC_URL}/${isDark ? 'b.png' : 'w.png'}`}
      alt="Sports App"
      sx={{
        height: { xs: 32, md: 44 },
        width: 'auto',
        maxWidth: { xs: 200, md: 280 },
        objectFit: 'contain',
      }}
    />
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        width: '100%',
        top: 0,
        backgroundColor: isDark ? '#000000' : '#ffffff',
        color: isDark ? '#ffffff' : '#1a202c',
        boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
        borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        height: { xs: '56px', md: '72px' },
      }}
    >
      <Toolbar
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 0,
          height: { xs: '56px', md: '72px' },
          minHeight: { xs: '56px !important', md: '72px !important' },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {/* Left: Menu (mobile) + brand / org */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            minWidth: 0,
            flex: 1,
            py: { xs: 0.5, md: 1.5 },
            zIndex: 1,
          }}
        >
          {onMenuClick && (
            <IconButton
              onClick={onMenuClick}
              aria-label="Open menu"
              sx={{
                color: isDark ? '#ffffff' : '#1a202c',
                mr: 0.5,
                p: { xs: 0.75, md: 1 },
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9' },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {isMasterOrg ? (
            <Box
              onClick={handleOpenSettings}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenSettings();
                }
              }}
              aria-label="Open account settings"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minWidth: 0,
                cursor: 'pointer',
                borderRadius: '8px',
                px: 0.5,
                py: 0.5,
                ml: -0.5,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                },
              }}
            >
              <Avatar
                src={profileImage}
                alt="Organisation logo"
                sx={{
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                  bgcolor: isDark ? '#6366f1' : '#1976d2',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {userInitial}
              </Avatar>
              <Typography
                noWrap
                sx={{
                  fontSize: { xs: '0.95rem', md: '1.125rem' },
                  fontWeight: 700,
                  lineHeight: 1.25,
                  letterSpacing: '-0.02em',
                  fontFamily: "'Inter', sans-serif",
                  color: isDark ? '#f8fafc' : '#0f172a',
                  maxWidth: { xs: 140, sm: 220, md: 320 },
                }}
              >
                {orgName || 'Club Partner'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Avatar
                src={profileImage}
                alt="Organisation logo"
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  width: 36,
                  height: 36,
                  bgcolor: isDark ? '#6366f1' : '#1976d2',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {userInitial}
              </Avatar>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  justifyContent: 'center',
                  py: 0.5,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    fontFamily: "'Inter', sans-serif",
                    color: isDark ? '#f8fafc' : '#0f172a',
                  }}
                >
                  SPORTS APP
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: '0.6rem', md: '0.65rem' },
                    fontWeight: 600,
                    lineHeight: 1,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontFamily: "'Inter', sans-serif",
                    color: isDark ? '#94a3b8' : '#64748b',
                    marginTop: '2px',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {headerText}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Center: Sports App logo (master org) */}
        {isMasterOrg && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {sportsAppLogo}
          </Box>
        )}

        {/* Right: Theme Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, zIndex: 1 }}>
          <IconButton
            onClick={toggleColorMode}
            aria-label="Toggle theme"
            sx={{
              color: isDark ? '#ffffff' : '#1a202c',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #e2e8f0',
              borderRadius: '8px',
              width: { xs: 36, md: 40 },
              height: { xs: 36, md: 40 },
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f7fafc',
                transform: 'scale(1.05)',
              },
            }}
          >
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
